import { useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import SectionHeader from "@/components/SectionHeader";
import { User as UserIcon, KeyRound, CheckCircle2 } from "lucide-react";

export default function Profile() {
  const { user, refreshMe } = useAuth();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [info, setInfo] = useState({ profile: { name: user?.name || "", bio: user?.bio || "", motorcycle: user?.motorcycle || "" } });
  const [status, setStatus] = useState({ type: "", msg: "" });
  const [loading, setLoading] = useState(false);

  const submitPassword = async (e) => {
    e.preventDefault();
    setStatus({ type: "", msg: "" });
    if (form.next.length < 6) { setStatus({ type: "error", msg: "Hasło musi mieć min. 6 znaków." }); return; }
    if (form.next !== form.confirm) { setStatus({ type: "error", msg: "Hasła się nie zgadzają." }); return; }
    setLoading(true);
    try {
      // Verify current password by attempting a login (no dedicated endpoint)
      await api.post("/auth/login", { username: user.username, password: form.current });
      // Change password
      await api.put(`/users/${user.id}`, { password: form.next });
      setStatus({ type: "ok", msg: "Hasło zmienione pomyślnie." });
      setForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 401) {
        setStatus({ type: "error", msg: "Obecne hasło jest nieprawidłowe." });
      } else {
        setStatus({ type: "error", msg: formatErr(detail) || err.message });
      }
    } finally {
      setLoading(false);
    }
  };

  const submitProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put(`/users/${user.id}`, info.profile);
      await refreshMe();
      setStatus({ type: "ok", msg: "Profil zaktualizowany." });
    } catch (err) {
      setStatus({ type: "error", msg: formatErr(err.response?.data?.detail) || err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 py-12" data-testid="profile-page">
      <SectionHeader title="Mój profil" />

      <div className="sr-card p-6 mt-10 flex items-center gap-4" data-testid="profile-card">
        <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: "var(--sr-bg-3)", border: "2px solid var(--sr-fire)" }}>
          <UserIcon className="text-[var(--sr-fire)]" />
        </div>
        <div>
          <p className="font-display text-xl text-[var(--sr-cream)]">{user?.name}</p>
          <p className="text-xs font-nav uppercase tracking-widest" style={{ color: user?.role === "admin" ? "var(--sr-fire)" : "var(--sr-muted)" }}>
            {user?.role === "admin" ? "Administrator" : "Członek"} · {user?.username}
          </p>
        </div>
      </div>

      {status.msg && (
        <div
          className="mt-6 p-4 text-sm text-center flex items-center justify-center gap-2"
          style={{
            background: status.type === "ok" ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.12)",
            border: `1px solid ${status.type === "ok" ? "#22c55e" : "#ef4444"}`,
            color: status.type === "ok" ? "#86efac" : "#fca5a5",
          }}
          data-testid="profile-status"
        >
          {status.type === "ok" && <CheckCircle2 size={16} />} {status.msg}
        </div>
      )}

      {/* Profil */}
      <form onSubmit={submitProfile} className="sr-card p-6 mt-6" data-testid="profile-form">
        <h3 className="font-display text-xl text-[var(--sr-cream)] mb-4">Dane profilowe</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="sr-label">Imię i nazwisko / pseudonim</label><input className="sr-input" value={info.profile.name} onChange={(e) => setInfo({ profile: { ...info.profile, name: e.target.value }})} /></div>
          <div><label className="sr-label">Motocykl</label><input className="sr-input" value={info.profile.motorcycle} onChange={(e) => setInfo({ profile: { ...info.profile, motorcycle: e.target.value }})} /></div>
          <div className="sm:col-span-2"><label className="sr-label">Bio</label><input className="sr-input" value={info.profile.bio} onChange={(e) => setInfo({ profile: { ...info.profile, bio: e.target.value }})} /></div>
        </div>
        <button type="submit" disabled={loading} className="btn-ghost mt-4" data-testid="save-profile-btn">Zapisz profil</button>
      </form>

      {/* Hasło */}
      <form onSubmit={submitPassword} className="sr-card p-6 mt-6" data-testid="password-form">
        <h3 className="font-display text-xl text-[var(--sr-cream)] mb-4 flex items-center gap-2">
          <KeyRound size={20} className="text-[var(--sr-fire)]" /> Zmiana hasła
        </h3>
        <div className="space-y-4">
          <div><label className="sr-label">Obecne hasło</label><input type="password" required className="sr-input" value={form.current} onChange={(e) => setForm({ ...form, current: e.target.value })} data-testid="current-password" /></div>
          <div><label className="sr-label">Nowe hasło (min. 6 znaków)</label><input type="password" required minLength={6} className="sr-input" value={form.next} onChange={(e) => setForm({ ...form, next: e.target.value })} data-testid="new-password" /></div>
          <div><label className="sr-label">Powtórz nowe hasło</label><input type="password" required className="sr-input" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} data-testid="confirm-password" /></div>
        </div>
        <button type="submit" disabled={loading} className="btn-fire mt-6" data-testid="change-password-btn">
          {loading ? "Zapisywanie..." : "Zmień hasło"}
        </button>
      </form>
    </div>
  );
}
